import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Filter, Sparkles, X, CheckSquare, Pencil, Upload, Mic } from "lucide-react";

import { loadBallEntries } from "../../api/ballbyball.api";
import api from "../../api/axios";
import { aiTagMatch } from "../../api/matches.api";
import { uploadBallVideo } from "../../api/recordings.api";
import BallVideoPlayer from "../../components/ballbyball/BallVideoPlayer";
import EditBallDialog from "../../components/ballbyball/EditBallDialog";
import WalletBadge from "../../components/wallet/WalletBadge";
import useLowBalanceToast from "../../hooks/useLowBalanceToast";
import useWalletStore from "../../store/wallet.store";

// QC screen for a match — dense table of every ball + flag rows with missing
// data + quick-jump back to the scoring screen for re-coding. Each column
// header carries a multi-select filter popover so operators can drill into
// specific subsets (one stroke, one bowler, one wicket type, etc.) and
// AND-combine across columns.

// Extras display — shows the TYPE and the RUN COUNT side by side, so a
// wide that went for 5 (1 penalty + 4 running) reads "wd 5" rather than
// just "wd". Derives the active-type set from the actual bucket fields
// (wide_runs / no_ball_runs / bye_runs / leg_bye_runs) — the entries
// API doesn't ship is_wide / is_no_ball booleans separately, only the
// run-count integers.
const extrasDisplay = (sc) => {
  if (!sc) return "—";
  const parts = [];
  if ((sc.wide_runs || 0)     > 0) parts.push(`wd ${sc.wide_runs}`);
  if ((sc.no_ball_runs || 0)  > 0) parts.push(`nb ${sc.no_ball_runs}`);
  if ((sc.bye_runs || 0)      > 0) parts.push(`b ${sc.bye_runs}`);
  if ((sc.leg_bye_runs || 0)  > 0) parts.push(`lb ${sc.leg_bye_runs}`);
  return parts.length ? parts.join(" · ") : "—";
};

// Extras FILTER token — keeps the per-type categorical so the filter
// popover doesn't explode into one option per run-count. Operator
// picks "wd" and sees every wide regardless of run count.
const extrasFilterToken = (sc) => {
  if (!sc) return null;
  const tokens = [];
  if ((sc.wide_runs || 0)     > 0) tokens.push("wd");
  if ((sc.no_ball_runs || 0)  > 0) tokens.push("nb");
  if ((sc.bye_runs || 0)      > 0) tokens.push("b");
  if ((sc.leg_bye_runs || 0)  > 0) tokens.push("lb");
  return tokens.length ? tokens.join(" ") : null;
};

// Column config. `filterable: true` rows get a header-popover filter;
// `getValue` returns the canonical value to filter on (matches what the
// cell renders so the operator's selection lines up visually).
const COLUMNS = [
  {
    key: "ball", label: "Ball", filterable: false,
  },
  {
    key: "innings", label: "Inns", filterable: true,
    getValue: (e) => e.innings?.innings_number ?? null,
    format: (v) => v == null ? "—" : `Innings ${v}`,
  },
  {
    key: "striker", label: "Striker", filterable: true,
    getValue: (e) => e.participants?.striker?.short_name || e.participants?.striker?.name || null,
  },
  {
    key: "non_striker", label: "Non-striker", filterable: true,
    getValue: (e) => e.participants?.non_striker?.short_name || e.participants?.non_striker?.name || null,
  },
  {
    key: "bowler", label: "Bowler", filterable: true,
    getValue: (e) => e.participants?.bowler?.short_name || e.participants?.bowler?.name || null,
  },
  {
    key: "runs", label: "R", filterable: true,
    getValue: (e) => e.scoring?.runs_total ?? e.scoring?.runs_off_bat ?? 0,
    format: (v) => String(v),
  },
  {
    key: "extras", label: "Extras", filterable: true,
    getValue: (e) => extrasFilterToken(e.scoring || {}),
  },
  {
    key: "stroke", label: "Stroke", filterable: true,
    getValue: (e) => e.analytics?.stroke || null,
  },
  {
    key: "conn", label: "Connection", filterable: true,
    getValue: (e) => e.analytics?.shot_connection || null,
  },
  {
    key: "foot", label: "Foot", filterable: true,
    getValue: (e) => e.analytics?.foot_movement || null,
  },
  {
    key: "wagon", label: "Position", filterable: true,
    getValue: (e) => e.spatial?.structured_position || (e.spatial?.wagon_wheel_x != null ? "(pin set)" : null),
  },
  {
    key: "wicket", label: "Wicket", filterable: true,
    getValue: (e) => e.wicket?.wicket_type || null,
  },
  {
    key: "video", label: "Video", filterable: true,
    getValue: (e) => e.video?.video_source_id ? "Yes" : "No",
  },
  // 2026-05-28 — voice utterances per ball. Filterable by Yes/No so
  // the operator can pull up every voice-coded ball in one view (e.g.
  // to spot-check STT accuracy across an innings).
  {
    key: "voice", label: "Voice", filterable: true,
    getValue: (e) => (e.voice_utterances?.length || 0) > 0 ? "Yes" : "No",
  },
];

const FILTERS = [
  { key: "all",            label: "All" },
  { key: "missing-stroke", label: "Missing stroke" },
  { key: "missing-conn",   label: "Missing connection" },
  { key: "missing-wagon",  label: "Missing wagon" },
  { key: "with-video",     label: "Has video clip" },
  { key: "wickets",        label: "Wickets only" },
];

function ballPasses(filterKey, e) {
  if (filterKey === "all") return true;
  if (filterKey === "missing-stroke")  return !e.analytics?.stroke;
  if (filterKey === "missing-conn")    return !e.analytics?.shot_connection;
  if (filterKey === "missing-wagon")   return !e.spatial?.structured_position && e.spatial?.wagon_wheel_x == null;
  if (filterKey === "with-video")      return !!e.video?.video_source_id;
  if (filterKey === "wickets")         return !!e.wicket?.wicket_type;
  return true;
}

export default function BallQCPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [tagging, setTagging] = useState(false);
  const [tagToast, setTagToast] = useState(null);
  // Per-column multi-select filters. Shape: { columnKey: ["val1","val2",...] }.
  // An empty / missing array = no filter on that column (all values pass).
  const [columnFilters, setColumnFilters] = useState({});
  // Which column's filter popover is currently open. Single global state
  // so opening one closes the others.
  const [openFilterCol, setOpenFilterCol] = useState(null);
  // Bulk-edit selection state. selectedIds is a Set of ball UUIDs the
  // operator has ticked for batch update. Empty = nothing selected =
  // bulk-edit panel hidden.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState("");
  // Single-ball edit modal — opens EditBallDialog from any QC row so the
  // operator doesn't have to navigate back to the live-coding screen.
  const [editingBallId, setEditingBallId] = useState(null);
  const [editingInningsId, setEditingInningsId] = useState(null);
  // Per-ball post-hoc clip-upload state.
  // {[ballId]: {phase:"idle"|"uploading"|"done"|"error", progress:0..100, msg?:string}}
  const [uploadState, setUploadState] = useState({});
  const { toastMsg: lowBalToast, dismiss: dismissLowBal, checkAndWarn } = useLowBalanceToast();

  const handleUploadClip = async (ballId, file) => {
    setUploadState((s) => ({ ...s, [ballId]: { phase: "uploading", progress: 0 } }));
    try {
      await uploadBallVideo(ballId, file, {
        onProgress: (p) =>
          setUploadState((s) => ({ ...s, [ballId]: { phase: "uploading", progress: p } })),
      });
      setUploadState((s) => ({ ...s, [ballId]: { phase: "done", progress: 100 } }));
      await fetchEntries();
      setTimeout(() => {
        setUploadState((s) => {
          const next = { ...s };
          delete next[ballId];
          return next;
        });
      }, 1800);
    } catch (e) {
      const msg = e?.response?.data?.message
        || e?.response?.data?.error
        || e?.message
        || "Upload failed";
      setUploadState((s) => ({ ...s, [ballId]: { phase: "error", progress: 0, msg } }));
    }
  };

  const runAiTag = async () => {
    if (tagging) return;
    setTagging(true);
    try {
      const res = await aiTagMatch(id, { fill_missing_only: true, max: 200 });
      const { tagged = 0, skipped = 0, error } = res.data || {};
      if (error) setTagToast({ ok: false, msg: `AI Tag error: ${error}` });
      else setTagToast({ ok: true, msg: `AI tagged ${tagged} balls (${skipped} already complete). Refreshing…` });
      await fetchEntries();
      useWalletStore.getState().fetchBalance().then(() => checkAndWarn());
    } catch (e) {
      setTagToast({ ok: false, msg: e?.response?.data?.detail || e.message || "AI Tag failed" });
    } finally {
      setTagging(false);
      setTimeout(() => setTagToast(null), 4500);
    }
  };

  const fetchEntries = async () => {
    if (!id) return;
    setLoading(true); setError("");
    try {
      const res = await loadBallEntries(id);
      setEntries(res.data?.entries || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load ball entries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntries(); }, [id]);

  // Distinct values per filterable column, sorted, with counts.
  //
  // FACET FILTERING: each column's popover options are computed from
  // entries that pass EVERY OTHER COLUMN'S filter (and the quick-
  // filter chip), but NOT its own. This way picking Striker=Virat
  // narrows the Extras popover to the extras values actually present
  // in Virat's balls, and the counts reflect how many of the
  // currently-narrowed set hit each value. Previously the options
  // were computed off the full unfiltered entries, so picking a
  // striker showed all extras values across all balls — operators
  // would tick "wd" expecting Virat's wides and get zero rows because
  // Virat never faced a wide; the filter dropdowns weren't co-related.
  const filterOptions = useMemo(() => {
    const out = {};
    for (const col of COLUMNS) {
      if (!col.filterable) continue;
      // Entries passing every filter EXCEPT this column's.
      const peerFiltered = entries.filter((e) => {
        if (!ballPasses(filter, e)) return false;
        for (const other of COLUMNS) {
          if (!other.filterable) continue;
          if (other.key === col.key) continue;   // skip own filter
          const sel = columnFilters[other.key];
          if (!sel || sel.length === 0) continue;
          const v = other.getValue(e);
          if (!sel.includes(v)) return false;
        }
        return true;
      });
      const counts = new Map();
      for (const e of peerFiltered) {
        const v = col.getValue(e);
        if (v == null || v === "") continue;
        counts.set(v, (counts.get(v) || 0) + 1);
      }
      out[col.key] = Array.from(counts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => {
          if (typeof a.value === "number" && typeof b.value === "number") {
            return a.value - b.value;
          }
          return String(a.value).localeCompare(String(b.value));
        });
    }
    return out;
  }, [entries, filter, columnFilters]);

  // Apply BOTH the quick-filter chip AND every active column filter (AND
  // across columns; OR within a column).
  const visible = useMemo(() => {
    return entries.filter((e) => {
      if (!ballPasses(filter, e)) return false;
      for (const col of COLUMNS) {
        if (!col.filterable) continue;
        const selected = columnFilters[col.key];
        if (!selected || selected.length === 0) continue;
        const v = col.getValue(e);
        if (!selected.includes(v)) return false;
      }
      return true;
    });
  }, [entries, filter, columnFilters]);

  const counts = useMemo(() => {
    const c = { all: entries.length };
    FILTERS.forEach((f) => {
      if (f.key === "all") return;
      c[f.key] = entries.filter((e) => ballPasses(f.key, e)).length;
    });
    return c;
  }, [entries]);

  const setColumnFilter = (columnKey, values) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (!values || values.length === 0) {
        delete next[columnKey];
      } else {
        next[columnKey] = values;
      }
      return next;
    });
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setOpenFilterCol(null);
  };

  const activeFilterCount = Object.values(columnFilters).reduce(
    (acc, vals) => acc + (vals?.length ? 1 : 0),
    0,
  );

  const Cell = ({ children, mono, className = "" }) => (
    <td className={`px-2 py-1.5 align-top ${mono ? "font-mono" : ""} ${className}`}>{children}</td>
  );

  return (
    <div className="min-h-screen bg-bg-main text-text-primary">
      <div className="sticky top-0 z-10 bg-bg-primary border-b border-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/ball/scoring/${id}`)}
              className="px-3 py-1.5 rounded-md bg-bg-secondary text-text-primary text-xs uppercase tracking-wider"
              title="Return to the coding screen"
            >
              ← Back to coding
            </button>
            <div>
              <div className="text-base font-semibold">Ball-by-Ball QC</div>
              <div className="text-[10px] text-text-secondary">
                Review every ball, filter by missing fields, jump back to scoring.
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <WalletBadge />
            <button
              onClick={fetchEntries}
              disabled={loading}
              className="px-3 py-1.5 rounded-md bg-bg-secondary text-text-primary text-xs uppercase tracking-wider disabled:opacity-60"
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 pb-2 flex flex-wrap items-center gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md border transition-colors ${
                filter === f.key
                  ? "border-button-primary bg-button-primary/15 text-text-primary"
                  : "border-bg-secondary bg-bg-secondary/40 text-text-secondary hover:text-text-primary"
              }`}
            >
              {f.label} <span className="opacity-70">({counts[f.key] ?? 0})</span>
            </button>
          ))}
          {activeFilterCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20"
              title="Clear every column filter"
            >
              <X className="w-3 h-3" />
              Clear {activeFilterCount} column filter{activeFilterCount === 1 ? "" : "s"}
            </button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        {/* Mini-timeline strip — most recent 30 balls as colored
            squares (mirrors ScoreRibbon's recent-balls block). Click
            any square to scroll to that row in the table. Gives a
            reviewer a one-glance gestalt of the last few overs without
            scrolling the full ball list. */}
        {entries.length > 0 && (
          <div className="mb-3 flex items-center gap-1 overflow-x-auto pb-1">
            <span className="text-[10px] uppercase tracking-widest text-text-secondary mr-2 shrink-0">
              Last {Math.min(30, entries.length)}
            </span>
            {entries.slice(-30).map((e, i) => {
              const sc = e.scoring || {};
              // 2026-05-28 — was reading e.dismissal which the API doesn't
              // emit. Canonical field is e.wicket (per serialize_player /
              // list_match_ball_entries). The timeline never coloured
              // wicket balls red because of this.
              const wkt = !!e.wicket?.wicket_type;
              const ot = sc.overthrow_runs || 0;
              const r  = sc.runs_total ?? sc.runs_off_bat ?? 0;
              const color =
                wkt              ? "bg-red-600 text-white"
                : ot > 0         ? "bg-violet-500/40 text-violet-100"
                : r === 6        ? "bg-purple-600 text-white"
                : r === 4        ? "bg-emerald-500 text-white"
                : (sc.wide_runs || sc.no_ball_runs || sc.bye_runs || sc.leg_bye_runs) > 0
                                 ? "bg-amber-500/30 text-amber-200"
                : r > 0          ? "bg-bg-secondary text-text-primary"
                                 : "bg-bg-secondary/50 text-text-secondary";
              // Overthrow label: "<off-bat>+<overthrow>" — runs_off_bat
              // already includes the overthrow portion (founder model
              // 2026-05-30), so physical off-bat = runs_off_bat - ot.
              const label = wkt
                ? "wk"
                : (ot > 0
                    ? `${Math.max(0, (sc.runs_off_bat || 0) - ot)}+${ot}`
                    : (r === 6 ? "6" : (r === 4 ? "4" : String(r))));
              return (
                <button
                  key={e.id}
                  onClick={() => {
                    setExpandedId(e.id);
                    // Scroll the ball row into view if rendered.
                    const row = document.getElementById(`qc-row-${e.id}`);
                    if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                  // 2026-05-28 — over_number / ball_in_over are nested
                  // under e.sequence in the API response, not top-level.
                  // The tooltip used to read "0.undefined" for every ball.
                  title={`${e.sequence?.display || ""} — ${e.participants?.striker?.name || ""}`}
                  className={`min-w-[24px] h-6 rounded text-[10px] font-mono flex items-center justify-center shrink-0 hover:ring-2 hover:ring-button-primary/60 ${color}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Bulk-edit toolbar — appears only when ≥1 ball is selected
            via the per-row checkbox. Lets the operator fix the same
            field across N balls in one shot (the classic "wrong bowler
            tagged on a full over" case). */}
        {selectedIds.size > 0 && (
          <div className="mb-3 flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
            <CheckSquare className="w-4 h-4 text-amber-300" />
            <span className="text-amber-200">
              {selectedIds.size} ball{selectedIds.size === 1 ? "" : "s"} selected
            </span>
            <button
              onClick={() => setBulkEditOpen(true)}
              className="ml-2 px-3 py-1 rounded bg-amber-400/30 text-amber-100 border border-amber-400/60 text-xs uppercase tracking-wider hover:bg-amber-400/40"
            >
              Bulk edit →
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="ml-1 px-3 py-1 rounded bg-bg-secondary text-text-secondary border border-border-default text-xs uppercase tracking-wider hover:bg-bg-main"
            >
              Clear
            </button>
          </div>
        )}

        {error && <div className="text-red-400 text-sm mb-3">{error}</div>}
        {!loading && entries.length === 0 && !error && (
          <div className="text-text-secondary text-sm">No ball entries recorded yet.</div>
        )}

        {(() => {
          const missing = (counts["missing-stroke"] || 0)
            + (counts["missing-conn"] || 0)
            + (counts["missing-wagon"] || 0);
          if (missing === 0 || entries.length === 0) return null;
          return (
            <div className="mb-4 rounded-lg border border-blue-500/40 bg-blue-500/10 p-3 flex items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="w-4 h-4 shrink-0 text-blue-300" />
                <span className="text-blue-100">
                  <strong>{missing}</strong> field-gaps spotted across this match.{" "}
                  <span className="opacity-90">
                    AI Tag can fill them in one shot — 15 Zencredits.
                  </span>
                </span>
              </div>
              <button
                onClick={runAiTag}
                disabled={tagging}
                className="shrink-0 text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-md bg-blue-500 hover:bg-blue-400 text-white disabled:opacity-50"
              >
                {tagging ? "Tagging…" : "Run AI Tag · 15 ZC"}
              </button>
            </div>
          );
        })()}

        {tagToast && (
          <div
            className={`mb-3 rounded-md border px-3 py-2 text-sm ${
              tagToast.ok
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                : "border-rose-500/40 bg-rose-500/10 text-rose-200"
            }`}
          >
            {tagToast.msg}
          </div>
        )}

        {lowBalToast && (
          <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 px-3 py-2 text-sm flex items-center justify-between gap-3">
            <span>⚠ {lowBalToast}</span>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => navigate("/account/topup")}
                className="text-[11px] uppercase tracking-wider px-2 py-1 rounded bg-amber-500 text-black hover:bg-amber-400"
              >
                Top up
              </button>
              <button
                onClick={dismissLowBal}
                className="text-amber-300 hover:text-amber-100 px-1"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto rounded-lg border border-bg-secondary">
          <table className="w-full text-xs">
            <thead className="bg-bg-secondary/40 text-text-secondary uppercase tracking-wider text-[10px]">
              <tr>
                {COLUMNS.map((h) => (
                  <th key={h.key} className="px-2 py-1.5 text-left whitespace-nowrap">
                    <ColumnHeader
                      column={h}
                      options={filterOptions[h.key] || []}
                      selected={columnFilters[h.key] || []}
                      isOpen={openFilterCol === h.key}
                      onOpen={() => setOpenFilterCol(openFilterCol === h.key ? null : h.key)}
                      onClose={() => setOpenFilterCol(null)}
                      onChange={(vals) => setColumnFilter(h.key, vals)}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((e) => {
                const an = e.analytics || {};
                const sp = e.spatial || {};
                const wk = e.wicket || {};
                const vd = e.video || {};
                const sc = e.scoring || {};
                const isOpen = expandedId === e.id;
                const missingStroke = !an.stroke;
                const missingConn = !an.shot_connection;
                const missingWagon = !sp.structured_position && sp.wagon_wheel_x == null;
                const flagged = missingStroke || missingConn || missingWagon;
                return (
                  <Fragment key={e.id}>
                  <tr
                    id={`qc-row-${e.id}`}
                    className={`border-t border-bg-secondary/60 hover:bg-bg-secondary/30 cursor-pointer ${
                      flagged ? "bg-amber-500/5" : ""
                    } ${selectedIds.has(e.id) ? "bg-amber-500/10" : ""}`}
                    onClick={() => setExpandedId(isOpen ? null : e.id)}
                  >
                    <Cell mono>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(e.id)}
                        onClick={(ev) => ev.stopPropagation()}
                        onChange={(ev) => {
                          // Bulk-edit selection. stopPropagation above
                          // keeps the checkbox click from ALSO toggling
                          // the expand-row click handler.
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (ev.target.checked) next.add(e.id);
                            else next.delete(e.id);
                            return next;
                          });
                        }}
                        className="mr-2 align-middle accent-amber-400"
                      />
                      {/* 2026-05-28 — use the backend's pre-computed
                          display field (floors over_number-1 at 0).
                          The previous client-side math would emit
                          "-1.X" if over_number was 0 — defensive. */}
                      {e.sequence?.display || "—"}
                    </Cell>
                    <Cell>{e.innings?.innings_number ?? "—"}</Cell>
                    <Cell>{e.participants?.striker?.short_name || e.participants?.striker?.name || "—"}</Cell>
                    {/* 2026-05-28 — non-striker cell was missing; every
                        column from "Non-striker" rightward was rendering
                        one column to the left of its header. */}
                    <Cell>{e.participants?.non_striker?.short_name || e.participants?.non_striker?.name || "—"}</Cell>
                    <Cell>{e.participants?.bowler?.short_name || e.participants?.bowler?.name || "—"}</Cell>
                    <Cell mono>{sc.runs_total ?? sc.runs_off_bat ?? 0}</Cell>
                    <Cell>{extrasDisplay(sc)}</Cell>
                    <Cell className={missingStroke ? "text-amber-400" : ""}>
                      {an.stroke || "—"}
                    </Cell>
                    <Cell className={missingConn ? "text-amber-400" : ""}>
                      {an.shot_connection || "—"}
                    </Cell>
                    <Cell>{an.foot_movement || "—"}</Cell>
                    <Cell className={missingWagon ? "text-amber-400" : ""}>
                      {sp.structured_position || (sp.wagon_wheel_x != null ? "(pin set)" : "—")}
                    </Cell>
                    <Cell>{wk.wicket_type || "—"}</Cell>
                    <Cell>
                      {vd.video_source_id ? (
                        <span className="text-emerald-400">●</span>
                      ) : (
                        <span className="text-text-secondary/40">—</span>
                      )}
                    </Cell>
                    {/* 2026-05-28 — voice utterances column. Shows count
                        + a quick 🎙 indicator. The inline <audio> player +
                        transcript live in the row's expanded panel below
                        so the table stays dense at default. */}
                    <Cell>
                      {(e.voice_utterances?.length || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 text-purple-300">
                          <Mic className="w-3 h-3" />
                          <span className="text-[11px]">{e.voice_utterances.length}</span>
                        </span>
                      ) : (
                        <span className="text-text-secondary/40">—</span>
                      )}
                    </Cell>
                  </tr>
                  {isOpen && (
                    <tr className="bg-bg-secondary/20">
                      <td colSpan={COLUMNS.length} className="px-3 py-3">
                        {/* Per-row action bar — Edit ball data + Upload clip
                            (post-hoc, when no clip exists). Replaces the
                            "operator must navigate back to BBP to fix
                            anything" flow for QC review. */}
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <button
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setEditingInningsId(e.innings?.id ?? null);
                              setEditingBallId(e.id);
                            }}
                            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-1 rounded-md bg-blue-500/15 border border-blue-500/40 text-blue-300 hover:bg-blue-500/25"
                            title="Open the full ball editor for this delivery"
                          >
                            <Pencil className="w-3 h-3" />
                            Edit ball data
                          </button>
                          {!e.video?.video_source_id && (
                            <UploadClipButton
                              ballId={e.id}
                              state={uploadState[e.id]}
                              onUpload={(file) => handleUploadClip(e.id, file)}
                            />
                          )}
                          {uploadState[e.id]?.phase === "error" && (
                            <span className="text-[11px] text-rose-300">
                              {uploadState[e.id].msg}
                            </span>
                          )}
                        </div>

                        {/* Prefer the BallVideo sub-record offsets (set by the slicer
                            with frame-accurate values from the master) but fall back
                            to the top-level Ball.video_start_ms which the live recorder
                            stamps at ball-save time. Without the fallback, every ball
                            shows "No clip" until the Lambda runs — even though we have
                            a perfectly good local IDB chunk to play. */}
                        <ClipTimeframe
                          startMs={e.video?.video_start_ms ?? e.video_start_ms ?? null}
                          endMs={e.video?.video_end_ms ?? e.video_end_ms ?? null}
                        />
                        <BallVideoPlayer
                          ballId={e.id}
                          matchId={id}
                          ball={{
                            video_start_ms: e.video?.video_start_ms ?? e.video_start_ms ?? null,
                            video_end_ms:   e.video?.video_end_ms   ?? e.video_end_ms   ?? null,
                          }}
                        />
                        {/* 2026-05-28 — voice utterances expanded panel.
                            One <audio> element per utterance plus the
                            transcript, parsed kind, and operator
                            correction (if any). Audio served via signed
                            S3 URL (1h TTL — refresh by reloading the QC
                            page). Format is whatever the browser
                            captured (webm/opus on Chrome, mp4/aac on
                            Safari) — every modern browser plays both
                            natively in <audio>. */}
                        {(e.voice_utterances?.length || 0) > 0 && (
                          <div className="mt-3 border-t border-bg-secondary/60 pt-3">
                            <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-2 flex items-center gap-1">
                              <Mic className="w-3 h-3 text-purple-300" />
                              Voice commands ({e.voice_utterances.length})
                            </div>
                            <div className="space-y-2">
                              {e.voice_utterances.map((u) => (
                                <div
                                  key={u.id}
                                  className="flex items-start gap-2 text-[11px] bg-bg-primary/40 border border-bg-secondary rounded px-2 py-1.5"
                                >
                                  <audio
                                    controls
                                    preload="none"
                                    src={u.audio_url || undefined}
                                    className="h-7"
                                    style={{ minWidth: 180, maxWidth: 240 }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="font-mono text-text-primary break-words">
                                      “{u.transcript || <span className="text-text-secondary/60">(no transcript)</span>}”
                                    </div>
                                    <div className="text-text-secondary text-[10px] mt-0.5 flex flex-wrap gap-x-2">
                                      {u.parsed_kind && <span>parsed: {u.parsed_kind}</span>}
                                      {u.parsed_label && <span>→ {u.parsed_label}</span>}
                                      {u.stt_model && <span className="opacity-70">· {u.stt_model}</span>}
                                      {u.audio_duration_ms != null && (
                                        <span className="opacity-70">· {(u.audio_duration_ms / 1000).toFixed(1)}s</span>
                                      )}
                                    </div>
                                    {u.operator_correction && (
                                      <div className="text-amber-300 text-[10px] mt-0.5">
                                        Correction: “{u.operator_correction}”
                                      </div>
                                    )}
                                  </div>
                                  {u.audio_url && (
                                    <a
                                      href={u.audio_url}
                                      download={`utterance-${u.id}.${u.audio_content_type?.includes("mp4") ? "mp4" : "webm"}`}
                                      className="text-[10px] text-purple-300 hover:text-purple-200 underline shrink-0 mt-1"
                                      title="Download audio for training-data collection"
                                    >
                                      ↓
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {visible.length === 0 && entries.length > 0 && (
          <div className="text-text-secondary text-xs mt-3">
            No balls match this filter combination.
          </div>
        )}
      </div>

      <EditBallDialog
        open={!!editingBallId}
        onClose={() => { setEditingBallId(null); setEditingInningsId(null); }}
        matchId={id}
        inningsId={editingInningsId}
        initialBallId={editingBallId}
        players={null}
        onSaved={() => fetchEntries()}
      />

      {bulkEditOpen && (
        <BulkEditModal
          ballIds={Array.from(selectedIds)}
          entries={entries}
          saving={bulkSaving}
          error={bulkError}
          onClose={() => { setBulkEditOpen(false); setBulkError(""); }}
          onSubmit={async (updates) => {
            setBulkSaving(true);
            setBulkError("");
            try {
              await api.post("/api/scoring/balls/bulk-update/", {
                ball_ids: Array.from(selectedIds),
                updates,
              });
              await fetchEntries();
              setBulkEditOpen(false);
              setSelectedIds(new Set());
            } catch (e) {
              setBulkError(e?.response?.data?.detail || e?.message || "Bulk update failed");
            } finally {
              setBulkSaving(false);
            }
          }}
        />
      )}
    </div>
  );
}


// Tiny standalone modal for "apply same change to N balls". Only the
// fields safe to bulk-set: bowler, striker, non_striker. Anything
// that requires per-ball context (runs / extras / wicket sub-records)
// belongs in the per-ball EditBallDialog instead.
function BulkEditModal({ ballIds, entries, saving, error, onClose, onSubmit }) {
  const [field, setField] = useState("bowler");
  const [valueId, setValueId] = useState("");

  // Build a roster from the entries themselves — every distinct
  // striker / non_striker / bowler that appears in the current ball
  // list. Avoids a separate fetch + keeps the picker scoped to
  // players who actually appear in this match.
  const roster = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      [e.participants?.striker, e.participants?.non_striker, e.participants?.bowler]
        .filter(Boolean)
        .forEach((p) => {
          if (p.id && !m.has(p.id)) {
            m.set(p.id, { id: p.id, name: p.short_name || p.name || p.id });
          }
        });
    }
    return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  const canSubmit = !!valueId && !saving;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary shadow-2xl">
        <header className="px-5 py-3 border-b border-border-default flex items-center justify-between">
          <h3 className="text-base font-semibold text-text-primary">
            Bulk edit {ballIds.length} ball{ballIds.length === 1 ? "" : "s"}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-bg-secondary text-text-secondary">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="p-5 space-y-3 text-sm">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1">
              Field to update
            </label>
            <select
              value={field}
              onChange={(e) => { setField(e.target.value); setValueId(""); }}
              className="w-full"
            >
              <option value="bowler">Bowler</option>
              <option value="striker">Striker</option>
              <option value="non_striker">Non-striker</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1">
              New value
            </label>
            <select
              value={valueId}
              onChange={(e) => setValueId(e.target.value)}
              className="w-full"
            >
              <option value="">— Pick a player —</option>
              {roster.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          {error && <div className="text-rose-300 text-xs">{error}</div>}
        </div>
        <footer className="px-5 py-3 border-t border-border-default flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-xs bg-bg-secondary text-text-primary">
            Cancel
          </button>
          <button
            disabled={!canSubmit}
            onClick={() => onSubmit({ [field]: valueId })}
            className="px-3 py-1.5 rounded text-xs bg-amber-400/30 text-amber-100 border border-amber-400/60 disabled:opacity-50"
          >
            {saving ? "Applying…" : `Apply to ${ballIds.length}`}
          </button>
        </footer>
      </div>
    </div>
  );
}

// Per-row clip-upload helper. Used in QC when a ball has no recorded
// clip (live stream was offline / camera glitched / scorer started late)
// and the operator wants to retroactively attach a file from disk so the
// QC review is complete. Reuses the same VideoUploadView the BBP
// File-mode video player hits, so all the size/format/storage-cap
// validation stays in one place.
function UploadClipButton({ ballId, state, onUpload }) {
  const inputRef = useRef(null);
  const phase = state?.phase || "idle";

  if (phase === "uploading") {
    return (
      <div className="inline-flex items-center gap-2 text-[11px] px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/40 text-amber-200">
        <Upload className="w-3 h-3 animate-pulse" />
        Uploading… {state.progress ?? 0}%
      </div>
    );
  }
  if (phase === "done") {
    return (
      <div className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-200">
        ✓ Clip uploaded
      </div>
    );
  }
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.m4v"
        className="hidden"
        onChange={(ev) => {
          const f = ev.target.files?.[0];
          if (f) onUpload(f);
          ev.target.value = "";
        }}
      />
      <button
        onClick={(ev) => {
          ev.stopPropagation();
          inputRef.current?.click();
        }}
        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25"
        title="Attach a clip for this ball (missed during live recording)"
      >
        <Upload className="w-3 h-3" />
        Upload clip
      </button>
    </>
  );
}


// Column header cell — label + (when filterable) a filter icon that
// opens a popover with multi-select checkboxes for the distinct values.
// Clicking outside the popover closes it. Selected count badge on the
// filter icon shows how many values are active.
function ColumnHeader({ column, options, selected, isOpen, onOpen, onClose, onChange }) {
  const popoverRef = useRef(null);

  // Close on outside click — the cheapest "modal" behaviour without a
  // wrapping context. The mousedown listener sees outside clicks
  // BEFORE the click bubbles up.
  useEffect(() => {
    if (!isOpen) return;
    const handle = (ev) => {
      if (popoverRef.current && !popoverRef.current.contains(ev.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [isOpen, onClose]);

  if (!column.filterable) {
    return <span>{column.label}</span>;
  }

  const hasFilter = selected.length > 0;
  const toggleValue = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="relative inline-flex items-center gap-1">
      <span>{column.label}</span>
      <button
        type="button"
        onClick={(ev) => {
          ev.stopPropagation();
          onOpen();
        }}
        className={`p-0.5 rounded transition-colors ${
          hasFilter
            ? "text-button-primary bg-button-primary/15"
            : "text-text-secondary/60 hover:text-text-primary"
        }`}
        title={hasFilter
          ? `${selected.length} value${selected.length === 1 ? "" : "s"} selected — click to change`
          : `Filter by ${column.label.toLowerCase()}`}
      >
        <Filter className="w-3 h-3" />
        {hasFilter && (
          <span className="absolute -top-1 -right-1 text-[8px] font-bold leading-none px-1 py-0.5 rounded-full bg-button-primary text-white">
            {selected.length}
          </span>
        )}
      </button>
      {isOpen && (
        <div
          ref={popoverRef}
          className="absolute top-full left-0 mt-1 z-30 min-w-[200px] max-w-[280px] bg-bg-primary border border-border-default rounded-md shadow-xl p-2"
          onClick={(ev) => ev.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] uppercase tracking-wider text-text-secondary">
              Filter · {column.label}
            </span>
            <button
              type="button"
              onClick={() => onChange([])}
              disabled={!hasFilter}
              className="text-[10px] uppercase tracking-wider text-text-secondary hover:text-text-primary disabled:opacity-40"
            >
              Clear
            </button>
          </div>
          {options.length === 0 ? (
            <div className="text-[11px] text-text-secondary py-2 text-center">
              No values to filter on
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto -mr-1 pr-1">
              {options.map((opt) => {
                const label = column.format
                  ? column.format(opt.value)
                  : String(opt.value);
                const checked = selected.includes(opt.value);
                return (
                  <label
                    key={String(opt.value)}
                    className={`flex items-center justify-between gap-2 px-2 py-1 rounded text-xs normal-case tracking-normal cursor-pointer ${
                      checked ? "bg-button-primary/10 text-text-primary" : "hover:bg-bg-secondary/40"
                    }`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleValue(opt.value)}
                        className="shrink-0 accent-button-primary"
                      />
                      <span className="truncate" title={label}>{label}</span>
                    </span>
                    <span className="text-[10px] text-text-secondary shrink-0">
                      {opt.count}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Renders the master-video offsets for a ball clip as MM:SS → MM:SS (Ns).
// Shown above the per-ball player so the operator can see exactly which
// section of the master recording got sliced.
function ClipTimeframe({ startMs, endMs }) {
  if (startMs == null || endMs == null) {
    return (
      <div className="text-[11px] font-mono text-text-secondary/70 mb-2">
        Clip timeframe: — (no offsets recorded)
      </div>
    );
  }
  const fmt = (ms) => {
    const totalSec = Math.max(0, Math.round(ms / 1000));
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  };
  const durSec = ((endMs - startMs) / 1000).toFixed(1);
  return (
    <div className="text-[11px] font-mono text-text-secondary mb-2 flex items-center gap-2 flex-wrap">
      <span className="uppercase tracking-wider text-[10px] text-text-secondary/70">Clip:</span>
      <span className="text-emerald-300">{fmt(startMs)}</span>
      <span className="text-text-secondary/50">→</span>
      <span className="text-emerald-300">{fmt(endMs)}</span>
      <span className="text-text-secondary/70">({durSec}s)</span>
    </div>
  );
}
